import React, { useState } from 'react'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Chip,
  Progress,
  Input,
} from '@heroui/react'

import { useToast } from '../../../hooks'
import {
  migrateGifFileSizes,
  type FileSizeMigrationResult,
} from '../services/mediaManagementService'

export const FileSizeMigration: React.FC = () => {
  const [isRunning, setIsRunning] = useState(false)
  const [dryRun, setDryRun] = useState(true)
  const [forceUpdate, setForceUpdate] = useState(false)
  const [batchSize, setBatchSize] = useState('100')
  const [result, setResult] = useState<FileSizeMigrationResult | null>(null)
  const [progress, setProgress] = useState(0)

  const toast = useToast()

  const handleMigrate = async () => {
    setIsRunning(true)
    setProgress(0)
    setResult(null)

    try {
      const options = {
        dryRun,
        forceUpdate,
        batchSize: parseInt(batchSize, 10) || 100,
      }

      toast.info(
        dryRun
          ? 'Starting dry run migration...'
          : 'Starting migration (this may take a while)...'
      )

      // Simulate progress (actual progress would need backend streaming)
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 10, 90))
      }, 500)

      const migrationResult = await migrateGifFileSizes(options)

      clearInterval(progressInterval)
      setProgress(100)
      setResult(migrationResult)

      if (dryRun) {
        toast.success(
          `Dry run complete! Would update ${migrationResult.updated} GIFs`
        )
      } else {
        toast.success(
          `Migration complete! Updated ${migrationResult.updated} GIFs`
        )
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Migration failed'
      toast.error(errorMessage)
      setProgress(0)
    } finally {
      setIsRunning(false)
    }
  }

  const getStatusColor = (
    status: 'updated' | 'skipped' | 'failed'
  ): 'success' | 'default' | 'danger' => {
    switch (status) {
      case 'updated':
        return 'success'
      case 'skipped':
        return 'default'
      case 'failed':
        return 'danger'
    }
  }

  return (
    <div className="space-y-4">
      {/* Migration Control Card */}
      <Card>
        <CardHeader>
          <div>
            <h3 className="text-lg font-semibold">File Size Migration</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Add fileSizeBytes to existing GIF documents from Storage metadata
            </p>
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="space-y-3">
            <Checkbox isSelected={dryRun} onValueChange={setDryRun}>
              Dry Run (preview changes without modifying data)
            </Checkbox>

            <Checkbox
              isSelected={forceUpdate}
              onValueChange={setForceUpdate}
              isDisabled={dryRun}
            >
              Force Update (update even if fileSizeBytes exists)
            </Checkbox>

            <Input
              label="Batch Size"
              type="number"
              value={batchSize}
              onValueChange={setBatchSize}
              min="1"
              max="500"
              className="max-w-xs"
              description="Number of GIFs to process per batch"
            />
          </div>

          <div className="flex gap-2">
            <Button
              color="primary"
              onPress={handleMigrate}
              isLoading={isRunning}
              isDisabled={isRunning}
            >
              {dryRun ? 'Run Dry Run' : 'Start Migration'}
            </Button>

            {result && (
              <Button
                variant="flat"
                onPress={() => setResult(null)}
                isDisabled={isRunning}
              >
                Clear Results
              </Button>
            )}
          </div>

          {isRunning && (
            <div className="space-y-2">
              <Progress
                value={progress}
                color="primary"
                className="max-w-md"
                showValueLabel
              />
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Processing...
              </p>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Results Card */}
      {result && (
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">
              {result.dryRun ? 'Dry Run Results' : 'Migration Results'}
            </h3>
          </CardHeader>
          <CardBody className="space-y-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Total Documents
                </p>
                <p className="text-2xl font-bold">{result.totalDocuments}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Processed
                </p>
                <p className="text-2xl font-bold">{result.processed}</p>
              </div>
              <div>
                <p className="text-sm text-green-600 dark:text-green-400">
                  {result.dryRun ? 'Would Update' : 'Updated'}
                </p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {result.updated}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Skipped
                </p>
                <p className="text-2xl font-bold">{result.skipped}</p>
              </div>
              <div>
                <p className="text-sm text-red-600 dark:text-red-400">Failed</p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {result.failed}
                </p>
              </div>
            </div>

            {/* Detailed Results (show first 50) */}
            {result.results.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-semibold">
                  Detailed Results (showing first 50):
                </h4>
                <div className="max-h-96 overflow-y-auto space-y-1">
                  {result.results.slice(0, 50).map((item) => (
                    <div
                      key={item.gifId}
                      className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded text-sm"
                    >
                      <div className="flex items-center gap-2 flex-1">
                        <Chip
                          size="sm"
                          color={getStatusColor(item.status)}
                          variant="flat"
                        >
                          {item.status}
                        </Chip>
                        <code className="text-xs">{item.gifId}</code>
                      </div>
                      {item.fileSizeBytes !== undefined && (
                        <span className="text-xs text-slate-600 dark:text-slate-400">
                          {(item.fileSizeBytes / 1024 / 1024).toFixed(2)} MB
                        </span>
                      )}
                      {item.error && (
                        <span className="text-xs text-red-600 dark:text-red-400 truncate max-w-xs">
                          {item.error}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                {result.results.length > 50 && (
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    ... and {result.results.length - 50} more results
                  </p>
                )}
              </div>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  )
}

export default FileSizeMigration
